<template>
  <div class="leads-dashboard">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-account-multiple</v-icon>
                Leads Management
                <span class="page-inline-subtitle">Manage and track all your sales prospects</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <v-btn
                  color="success"
                  @click="createLeadDialog = true"
                  class="modern-btn mr-3"
                  elevation="2"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add Lead
                </v-btn>
                <v-btn
                  color="info"
                  @click="bulkImportDialog = true"
                  class="modern-btn mr-3"
                  elevation="2"
                >
                  <v-icon left>mdi-upload</v-icon>
                  Bulk Import
                </v-btn>
                <v-btn 
                  color="primary" 
                  @click="fetchLeads"
                  class="modern-btn"
                  elevation="2"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Cards -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3" v-for="stat in stats" :key="stat.title">
          <v-card 
            class="stats-card"
            elevation="4"
            hover
          >
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper" :class="`${stat.color}--bg`">
                <v-icon size="28" color="white">{{ stat.icon }}</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stat.value }}</div>
                <div class="stats-label">{{ stat.title }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Leads Table -->
      <v-row>
        <v-col cols="12">
          <v-card class="leads-table-card" elevation="6">
            <v-card-title class="modern-card-title">
              <div class="filters-header-container">
                <div class="filters-single-row">
                  <!-- Title Section -->
                  <div class="table-title-section">
                    <h2 class="table-title">
                      <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                      Leads Pipeline
                    </h2>
                    <div class="results-count" v-if="totalResults > 0">
                      <div class="count-display">
                        <span class="count-number">{{ totalResults }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Filters Section -->
                  <div class="filters-section">
                    <v-text-field
                      v-model="searchQuery"
                      placeholder="Search leads..."
                      prepend-inner-icon="mdi-magnify"
                      outlined
                      dense
                      hide-details
                      clearable
                      class="search-field mr-3"
                      @input="debouncedSearch"
                    ></v-text-field>
                    
                    <v-select
                      v-model="selectedStatus"
                      :items="statusFilters"
                      item-text="text"
                      item-value="value"
                      outlined
                      dense
                      hide-details
                      placeholder="Filter by Status"
                      class="filter-select mr-3"
                      clearable
                      @change="fetchLeads"
                    ></v-select>

                    <v-select
                      v-model="selectedTemperature"
                      :items="temperatureFilters"
                      item-text="text"
                      item-value="value"
                      outlined
                      dense
                      hide-details
                      placeholder="Filter by Temperature"
                      class="filter-select"
                      clearable
                      @change="fetchLeads"
                    ></v-select>
                  </div>
                </div>
              </div>
            </v-card-title>

            <v-data-table
              :headers="headers"
              :items="leads"
              :loading="loading"
              :server-items-length="totalResults"
              :options.sync="options"
              :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
              class="modern-table"
              @update:options="fetchLeads"
            >
              <template v-slot:[`item.sr_no`]="{ index }">
                <span class="serial-number">{{ ((options.page - 1) * options.itemsPerPage) + index + 1 }}</span>
              </template>

              <template v-slot:[`item.name`]="{ item }">
                <div class="lead-info">
                  <div class="lead-avatar">
                    <v-avatar size="32" :color="getLeadColor(item)">
                      <span class="white--text">{{ getInitials(item) }}</span>
                    </v-avatar>
                  </div>
                  <div class="lead-details">
                    <div class="lead-name">{{ item.full_name }}</div>
                    <div class="lead-email">{{ item.email }}</div>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.company`]="{ item }">
                <div class="company-info">
                  <div class="company-name">{{ item.company_name }}</div>
                  <div class="company-details" v-if="item.designation">
                    {{ item.designation }}
                    <span v-if="item.industry"> • {{ item.industry }}</span>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.score`]="{ item }">
                <div v-if="item.latest_score" class="score-display">
                  <v-chip
                    small
                    :color="getScoreColor(item.latest_score.overall_score)"
                    dark
                  >
                    {{ item.latest_score.overall_score }}/100
                  </v-chip>
                </div>
                <span v-else class="grey--text">Not Scored</span>
              </template>

              <template v-slot:[`item.temperature`]="{ item }">
                <div v-if="item.latest_score">
                  <v-chip
                    small
                    :color="getTemperatureColor(item.latest_score.temperature)"
                    dark
                  >
                    <v-icon left small>{{ getTemperatureIcon(item.latest_score.temperature) }}</v-icon>
                    {{ item.latest_score.temperature.toUpperCase() }}
                  </v-chip>
                </div>
                <span v-else class="grey--text">-</span>
              </template>

              <template v-slot:[`item.status`]="{ item }">
                <v-chip
                  small
                  :color="getStatusColor(item.status)"
                  dark
                >
                  {{ item.status.toUpperCase() }}
                </v-chip>
              </template>

              <template v-slot:[`item.source`]="{ item }">
                <v-chip
                  small
                  outlined
                  :color="getSourceColor(item.source)"
                >
                  <v-icon left small>{{ getSourceIcon(item.source) }}</v-icon>
                  {{ item.source.toUpperCase() }}
                </v-chip>
              </template>

              <template v-slot:[`item.created_at`]="{ item }">
                <span class="date-text">{{ formatDate(item.created_at) }}</span>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="action-buttons">
                  <v-btn
                    icon
                    small
                    color="primary"
                    @click="viewLead(item)"
                  >
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="orange"
                    @click="editLead(item)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="red"
                    @click="deleteLead(item)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Create Lead Dialog -->
    <v-dialog v-model="createLeadDialog" max-width="800px" persistent>
      <v-card class="create-lead-dialog">
        <v-card-title class="dialog-header">
          <span class="headline">Add New Lead</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeCreateDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="dialog-content">
          <v-form ref="createForm" v-model="createFormValid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="createForm.first_name"
                  label="First Name *"
                  :rules="[v => !!v || 'First name is required']"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="createForm.last_name"
                  label="Last Name *"
                  :rules="[v => !!v || 'Last name is required']"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="createForm.email"
                  label="Email *"
                  type="email"
                  :rules="[v => !!v || 'Email is required', v => /.+@.+\..+/.test(v) || 'Email must be valid']"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="createForm.phone"
                  label="Phone"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="createForm.company_name"
                  label="Company Name *"
                  :rules="[v => !!v || 'Company name is required']"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="createForm.designation"
                  label="Designation"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="createForm.industry"
                  label="Industry"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="createForm.company_size"
                  :items="companySizeOptions"
                  label="Company Size"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="createForm.location"
                  label="Location"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="createForm.source"
                  :items="sourceOptions"
                  label="Lead Source"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="createForm.notes"
                  label="Notes"
                  outlined
                  rows="3"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="closeCreateDialog">Cancel</v-btn>
          <v-btn 
            color="primary" 
            :loading="submitting"
            :disabled="!createFormValid"
            @click="createLead"
          >
            Create Lead
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Import Dialog -->
    <v-dialog v-model="bulkImportDialog" max-width="600px" persistent>
      <v-card class="bulk-import-dialog">
        <v-card-title class="dialog-header">
          <span class="headline">Bulk Import Leads</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeBulkImportDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="dialog-content">
          <div class="import-instructions mb-4">
            <h4>CSV Format Required:</h4>
            <p class="text-caption">
              Please ensure your CSV file has the following columns:<br>
              <code>first_name, last_name, email, company_name, phone, designation, industry, company_size, location</code>
            </p>
          </div>
          <v-file-input
            v-model="importFile"
            label="Select CSV File"
            accept=".csv"
            outlined
            dense
            prepend-icon="mdi-file-excel"
            show-size
          ></v-file-input>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="closeBulkImportDialog">Cancel</v-btn>
          <v-btn 
            color="primary" 
            :loading="importing"
            :disabled="!importFile"
            @click="bulkImportLeads"
          >
            Import Leads
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from '@/axios';
import { debounce } from 'lodash';

export default {
  name: "LeadsManagement",
  data() {
    return {
      loading: false,
      submitting: false,
      importing: false,
      leads: [],
      totalResults: 0,
      statsData: {},
      searchQuery: '',
      selectedStatus: null,
      selectedTemperature: null,
      createLeadDialog: false,
      bulkImportDialog: false,
      createFormValid: false,
      importFile: null,
      createForm: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company_name: '',
        designation: '',
        industry: '',
        company_size: '',
        location: '',
        source: 'manual',
        notes: ''
      },
      options: {
        page: 1,
        itemsPerPage: 15,
        sortBy: ['created_at'],
        sortDesc: [true]
      },
      headers: [
        { text: 'Sr. No.', value: 'sr_no', sortable: false, width: '80px' },
        { text: 'Lead', value: 'name', sortable: false, width: '250px' },
        { text: 'Company', value: 'company', sortable: false, width: '200px' },
        { text: 'Score', value: 'score', sortable: false, width: '100px' },
        { text: 'Temperature', value: 'temperature', sortable: false, width: '120px' },
        { text: 'Status', value: 'status', sortable: true, width: '100px' },
        { text: 'Source', value: 'source', sortable: true, width: '100px' },
        { text: 'Created', value: 'created_at', sortable: true, width: '120px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '120px' },
      ],
      statusFilters: [
        { text: 'New', value: 'new' },
        { text: 'Contacted', value: 'contacted' },
        { text: 'Qualified', value: 'qualified' },
        { text: 'Unqualified', value: 'unqualified' },
        { text: 'Converted', value: 'converted' },
      ],
      temperatureFilters: [
        { text: 'Hot', value: 'hot' },
        { text: 'Warm', value: 'warm' },
        { text: 'Cold', value: 'cold' },
      ],
      companySizeOptions: [
        '1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'
      ],
      sourceOptions: [
        'manual', 'webform', 'import', 'api', 'referral', 'social'
      ],
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 5000
      }
    };
  },
  computed: {
    stats() {
      return [
        {
          title: 'Total Leads',
          value: this.statsData.total_leads || 0,
          icon: 'mdi-account-multiple',
          color: 'blue'
        },
        {
          title: 'Hot Leads',
          value: this.statsData.hot_leads || 0,
          icon: 'mdi-fire',
          color: 'red'
        },
        {
          title: 'Qualified',
          value: this.statsData.qualified_leads || 0,
          icon: 'mdi-check-circle',
          color: 'green'
        },
        {
          title: 'Converted',
          value: this.statsData.converted_leads || 0,
          icon: 'mdi-trophy',
          color: 'orange'
        }
      ];
    },
    debouncedSearch() {
      return debounce(() => {
        this.options.page = 1;
        this.fetchLeads();
      }, 500);
    }
  },
  async mounted() {
    await this.fetchStats();
    await this.fetchLeads();
  },
  methods: {
    async fetchStats() {
      try {
        const response = await axios.get('leads/stats');
        this.statsData = response.data.data;
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    },

    async fetchLeads() {
      this.loading = true;
      try {
        const params = {
          page: this.options.page,
          per_page: this.options.itemsPerPage,
        };

        if (this.searchQuery) {
          params.search = this.searchQuery;
        }

        if (this.selectedStatus) {
          params.status = this.selectedStatus;
        }

        if (this.selectedTemperature) {
          params.temperature = this.selectedTemperature;
        }

        if (this.options.sortBy.length) {
          params.sort_by = this.options.sortBy[0];
          params.sort_desc = this.options.sortDesc[0];
        }

        const response = await axios.get('leads', { params });
        this.leads = response.data.data.data;
        this.totalResults = response.data.data.total;

      } catch (error) {
        console.error('Error fetching leads:', error);
        this.showErrorMessage('Failed to load leads. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    async createLead() {
      if (!this.$refs.createForm.validate()) return;

      this.submitting = true;
      try {
        await axios.post('leads', this.createForm);
        this.closeCreateDialog();
        this.fetchLeads();
        this.fetchStats();
        this.showSuccessMessage('Lead created successfully!');
      } catch (error) {
        console.error('Error creating lead:', error);
        this.showErrorMessage('Failed to create lead. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    async bulkImportLeads() {
      if (!this.importFile) return;

      // Parse CSV file (simplified version)
      this.importing = true;
      try {
        const text = await this.importFile.text();
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        const leads = [];

        for (let i = 1; i < lines.length; i++) {
          if (lines[i].trim()) {
            const values = lines[i].split(',').map(v => v.trim());
            const lead = {};
            headers.forEach((header, index) => {
              lead[header] = values[index] || '';
            });
            leads.push(lead);
          }
        }

        await axios.post('leads/bulk-import', { leads });
        this.closeBulkImportDialog();
        this.fetchLeads();
        this.fetchStats();
        this.showSuccessMessage(`Successfully imported ${leads.length} leads!`);

      } catch (error) {
        console.error('Error importing leads:', error);
        this.showErrorMessage('Failed to import leads. Please check file format.');
      } finally {
        this.importing = false;
      }
    },

    viewLead(lead) {
      this.$router.push(`/agents/leadranker-ai/leads/${lead.id}`);
    },

    editLead(lead) {
      // TODO: Implement edit functionality
      console.log('Edit lead:', lead);
    },

    async deleteLead(lead) {
      if (confirm(`Are you sure you want to delete ${lead.full_name}?`)) {
        try {
          await axios.delete(`leads/${lead.id}`);
          this.fetchLeads();
          this.fetchStats();
          this.showSuccessMessage('Lead deleted successfully!');
        } catch (error) {
          console.error('Error deleting lead:', error);
          this.showErrorMessage('Failed to delete lead. Please try again.');
        }
      }
    },

    closeCreateDialog() {
      this.createLeadDialog = false;
      this.createForm = {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company_name: '',
        designation: '',
        industry: '',
        company_size: '',
        location: '',
        source: 'manual',
        notes: ''
      };
      this.$refs.createForm?.resetValidation();
    },

    closeBulkImportDialog() {
      this.bulkImportDialog = false;
      this.importFile = null;
    },

    getLeadColor(lead) {
      const colors = ['blue', 'green', 'purple', 'orange', 'red'];
      const index = lead.id % colors.length;
      return colors[index];
    },

    getInitials(lead) {
      return (lead.first_name?.[0] || '') + (lead.last_name?.[0] || '');
    },

    getTemperatureColor(temperature) {
      const colors = { hot: 'red', warm: 'orange', cold: 'blue' };
      return colors[temperature] || 'grey';
    },

    getTemperatureIcon(temperature) {
      const icons = { hot: 'mdi-fire', warm: 'mdi-thermometer-plus', cold: 'mdi-snowflake' };
      return icons[temperature] || 'mdi-thermometer';
    },

    getScoreColor(score) {
      if (score >= 80) return 'red';
      if (score >= 60) return 'orange';
      if (score >= 40) return 'yellow';
      return 'grey';
    },

    getStatusColor(status) {
      const colors = {
        new: 'blue',
        contacted: 'orange',
        qualified: 'green',
        unqualified: 'red',
        converted: 'purple'
      };
      return colors[status] || 'grey';
    },

    getSourceColor(source) {
      const colors = {
        manual: 'blue',
        webform: 'green',
        import: 'orange',
        api: 'purple',
        referral: 'pink',
        social: 'cyan'
      };
      return colors[source] || 'grey';
    },

    getSourceIcon(source) {
      const icons = {
        manual: 'mdi-account-plus',
        webform: 'mdi-web',
        import: 'mdi-upload',
        api: 'mdi-api',
        referral: 'mdi-account-network',
        social: 'mdi-share-variant'
      };
      return icons[source] || 'mdi-source-branch';
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },

    showSuccessMessage(message) {
      this.snackbar = {
        show: true,
        message,
        color: 'success',
        timeout: 5000
      };
    },

    showErrorMessage(message) {
      this.snackbar = {
        show: true,
        message,
        color: 'error',
        timeout: 7000
      };
    }
  }
};
</script>

<style scoped>
/* Same styles as dashboard component for consistency */
.leads-dashboard {
  min-height: calc(100vh - 64px);
}

.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.page-title {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
  font-size: 28px;
  margin: 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  align-items: flex-start;
}

.page-inline-subtitle {
  font-size: 14px;
  color: #64748b;
  font-weight: 400;
  margin-top: 4px;
  -webkit-text-fill-color: #64748b;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Stats Cards */
.stats-card {
  background: white;
  border-radius: 16px;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
}

.stats-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(102, 126, 234, 0.15);
}

.stats-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stats-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.blue--bg { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.red--bg { background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%); }
.orange--bg { background: linear-gradient(135deg, #ffa502 0%, #ff7675 100%); }
.green--bg { background: linear-gradient(135deg, #00b894 0%, #00cec9 100%); }

.stats-text {
  flex: 1;
}

.stats-value {
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
  line-height: 1;
}

.stats-label {
  font-size: 14px;
  color: #64748b;
  margin-top: 4px;
}

/* Table Styles */
.leads-table-card {
  background: white;
  border-radius: 20px;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.modern-card-title {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(240, 147, 251, 0.05) 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
}

.filters-header-container {
  width: 100%;
}

.filters-single-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 20px;
}

.table-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.table-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.results-count {
  display: flex;
  align-items: center;
}

.count-display {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.filters-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-field {
  min-width: 200px;
}

.filter-select {
  min-width: 150px;
}

/* Lead Info */
.lead-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.lead-details {
  flex: 1;
}

.lead-name {
  font-weight: 600;
  color: #2d3748;
}

.lead-email {
  font-size: 12px;
  color: #64748b;
}

.company-info {
  display: flex;
  flex-direction: column;
}

.company-name {
  font-weight: 600;
  color: #2d3748;
}

.company-details {
  font-size: 12px;
  color: #64748b;
}

.serial-number {
  font-weight: 600;
  color: #64748b;
}

.date-text {
  font-size: 12px;
  color: #64748b;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

/* Modern Buttons */
.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  transition: all 0.3s ease !important;
}

.modern-btn:hover {
  transform: translateY(-2px);
}

/* Dialog Styles */
.dialog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.dialog-content {
  padding: 24px;
}

.dialog-actions {
  padding: 16px 24px;
}

.import-instructions {
  background: rgba(102, 126, 234, 0.05);
  padding: 16px;
  border-radius: 8px;
}
</style>